import React, { useEffect, useRef, useState } from "react";
import "./tournament-round-hole-assignment-list.scss";
import {
  CENTER,
  SPACE_BETWEEN,
  defaultBorders,
  defaultGapping,
  defaultLayouts,
  defaultPadding,
  defaultTypography,
  golfstatusColors,
  golfstatusSpacing,
  GSActionBar,
  GSButton,
  GSEmptyList,
  GSInput,
  GSItemList,
  GSLoadingSpinnerOverlay,
  GSSelect,
  useMediaQuery,
} from "golfstatus_react_components";
import {
  faPlus,
  faSearch,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import TournamentRoundHoleAssignmentListItem from "./tournament-round-hole-assignment-list-item";
import { useMatch, useNavigate } from "react-router-dom";
import {
  selectLoading,
  selectTournamentTeams,
} from "../../../../reducers/teamsSlice";
import {
  addAdditionalHoleAssignment,
  selectCurrentRound,
  selectCurrentRoundHoleAssignments,
  selectLinkedRoundHoleAssignments,
  selectLoading as selectRoundLoading,
} from "../../../../reducers/roundSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectTournamentFlights } from "../../../../reducers/flightSlice";
import { filterUnassignedTeams, getTeamFlight, groupShotgunHoleAssignments, groupTeeTimeHoleAssignments } from "../../../../hooks/round/roundHoleAssignmentHooks";
import { isSameNineTwice } from "../../../../hooks/round/roundHooks";

//stands in for the add tee times row so the list can tell it from an assignment
const addTeeTimesItem = { addTeeTimes: true };

const TournamentRoundHoleAssignmentList = (props) => {
  const { style } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const match = useMatch("/:tournamentID/rounds/:roundID/hole-assignments");
  const { isPhone } = useMediaQuery();

  // local state
  const [search, setSearch] = useState("");
  const [holeFilter, setHoleFilter] = useState(null);
  const listRef = useRef(null);
  const addTeeTimesRef = useRef(null);
  const addedTeeTimesRef = useRef(false);

  // selectors
  const currentRound = useSelector(selectCurrentRound);
  const currentRoundHoleAssignments = useSelector(
    selectCurrentRoundHoleAssignments,
  );
  const linkedRoundHoleAssignments = useSelector(
    selectLinkedRoundHoleAssignments,
  );
  const tournamentTeams = useSelector(selectTournamentTeams);
  const flights = useSelector(selectTournamentFlights);
  const loading = useSelector(selectLoading);
  const roundLoading = useSelector(selectRoundLoading);
  //the request is a quick one, so it spins on the button itself rather than
  //taking the page over
  const addingTeeTimes = roundLoading?.includes?.(
    "addAdditionalHoleAssignment",
  );

  //a round opens at the top rather than wherever the last one was left off. the
  //assignments list is the one that scrolls, and it is this element's own child
  //rather than one of the lists the items and empty messages bring with them.
  useEffect(() => {
    const scroller = listRef.current?.querySelector?.(":scope > gs-item-list");

    if (scroller) {
      scroller.scrollTop = 0;
    }
  }, [currentRound?.id]);

  //new tee times land at the end of the list, out of sight below the fold. the
  //list follows them down so they are the first thing read, and the add lands
  //back under the cursor for another one.
  useEffect(() => {
    if (addingTeeTimes) {
      addedTeeTimesRef.current = true;
      return;
    }

    if (!addedTeeTimesRef.current) {
      return;
    }

    addedTeeTimesRef.current = false;
    addTeeTimesRef.current?.scrollIntoView?.({
      block: "end",
      behavior: "smooth",
    });
  }, [addingTeeTimes]);

  const getUnassignedTeams = () => {
    //a team assigned in a linked round is spoken for here too
    const assignments = [
      ...currentRoundHoleAssignments,
      ...linkedRoundHoleAssignments,
    ];

    return filterUnassignedTeams(tournamentTeams, assignments)
      .map((team) => ({ ...team, flight: getTeamFlight(team, flights) }))
      .sort((a, b) => a.mainFlightPosition - b.mainFlightPosition);
  };

  const getNumberOfAssignedTeams = () => {
    return (
      currentRoundHoleAssignments
        ?.flatMap?.((rha) => rha?.roundHoleAssignmentTournamentTeams ?? [])
        ?.filter?.((rhatt) => rhatt?.tournamentTeamKey)?.length ?? 0
    );
  };

  //a blocked spot can't take a team, so it isn't a slot left to fill
  const getNumberOfOpenSpots = () => {
    const spots =
      currentRoundHoleAssignments?.reduce?.(
        (total, rha) => total + (rha?.locked ? 0 : rha?.maxTeamsPerHole ?? 0),
        0,
      ) ?? 0;

    return Math.max(spots - getNumberOfAssignedTeams(), 0);
  };

  const getAssignedHeader = () => {
    const openSpots = getNumberOfOpenSpots();

    return (
      <div
        style={{
          ...defaultLayouts.hStack.align(SPACE_BETWEEN, CENTER),
          ...defaultLayouts.fullWidth,
          ...defaultGapping.mediumGap,
        }}
      >
        <div>{`Assigned (${getNumberOfAssignedTeams()})`}</div>
        <div style={{ ...defaultTypography.bodyRegular.withOpacity(0.6) }}>
          {openSpots > 0 ? `${openSpots} Slots Available` : "All Holes Assigned"}
        </div>
      </div>
    );
  };

  const getHoleAssignmentSearchString = (holeAssignment) => {
    let searchString = "";

    holeAssignment.forEach((position) => {
      if (
        searchString.length === 0 &&
        currentRound?.startType === "shotgun_start"
      ) {
        searchString += `hole ${position.hole} `;
      }
      position.roundHoleAssignmentTournamentTeams.forEach((rhatt) => {
        //a spot names its team by key, the names are in the teams list
        const team =
          tournamentTeams?.find?.((t) => t?.id === rhatt.tournamentTeamKey) ??
          rhatt;
        const teamName =
          team.name?.toLowerCase() || team.contactName?.toLowerCase();
        const playerNames = (team.players ?? [])
          .map((p) => p.name.toLowerCase())
          .join(" ");

        searchString += teamName + " " + playerNames;
      });
    });

    return searchString;
  };

  const applySearchTo = (assignments) => {
    if (search === "" && !holeFilter) {
      return assignments;
    }

    let searchResults = assignments;

    // if two tee, filter by hole first if included
    if (currentRound?.startType === "two_tee_interval_start" && holeFilter) {
      searchResults = searchResults.filter((a) => {
        return (
          a[0].hole === holeFilter.hole &&
          a[0].roundCourse?.course?.name === holeFilter.course
        );
      });
    }

    // search by team name or player name
    searchResults = searchResults?.filter?.((ha) => {
      let searchString = getHoleAssignmentSearchString(ha);
      return searchString.includes(search.toLowerCase());
    });

    return searchResults;
  };

  //locked assignments come through with the rest. the list item skips them when
  //it renders and needs them to know which group opens back up next.
  const getFilteredHoleAssignmentsForShotgunStart = () => {
    return applySearchTo(
      groupShotgunHoleAssignments(currentRoundHoleAssignments),
    );
  };

  const getFilteredHoleAssignmentsForTeeTimeStart = () => {
    return applySearchTo(
      groupTeeTimeHoleAssignments(currentRoundHoleAssignments),
    );
  };

  const getFilteredHoleAssignments = () => {
    if (currentRound?.startType === "shotgun_start") {
      return getFilteredHoleAssignmentsForShotgunStart();
    } else {
      return getFilteredHoleAssignmentsForTeeTimeStart();
    }
  };

  const getListItems = () => {
    const holeAssignments = getFilteredHoleAssignments();

    if (holeAssignments.length > 0 && isIntervalStart()) {
      return [...holeAssignments, addTeeTimesItem];
    }

    return holeAssignments;
  };

  const getHoleAssignment = (holeAssignments) => {
    if (holeAssignments === addTeeTimesItem) {
      return getAddTeeTimes();
    }

    return (
      <TournamentRoundHoleAssignmentListItem
        key={holeAssignments?.[0]?.id}
        holeAssignments={holeAssignments}
        assignedOnly={false}
        readOnly={false}
      />
    );
  };

  const getNoTeamsEmptyListMessage = () => {
    return (
      <GSEmptyList
        title="Assign Players & Teams"
        detail="This tournament does not have any players or teams."
        actions={[
          {
            title: "Add Team",
            onClick: () =>
              navigate(`/${match?.params?.tournamentID}/teams/add`),
            type: "black",
            buttonIcon: faPlus,
          },
        ]}
      />
    );
  };

  const getNoSearchResultsEmptyListMessage = () => {
    return (
      <GSEmptyList
        title="We couldn’t find who you’re looking for…"
        detail="Try changing the search term or review the players and teams list."
        actions={[
          {
            title: "Clear Search",
            onClick: () => {
              setSearch("");
            },
            type: "grey",
          },
          {
            title: "View Players & Teams",
            onClick: () => navigate(`/${match?.params?.tournamentID}/teams`),
            type: "full-width black no-wrap",
          },
        ]}
      />
    );
  };

  const getTwoTeeHoleOptionsForRoundCourse = (roundCourses) => {
    // if the round courses are the same 9 holes, we shouldn't display the hole options
    if (
      !currentRound?.roundCourses ||
      isSameNineTwice(currentRound?.roundCourses)
    ) {
      return null;
    }

    let firstHole = null;
    let secondHole = null;
    let firstCourse = null;
    let secondCourse = null;

    if (roundCourses?.length === 1) {
      const roundCourse = roundCourses[0];
      firstCourse = roundCourse.course.name;
      secondCourse = roundCourse.course.name;
      if (roundCourse.numberOfHoles === 18) {
        firstHole = 1;
        secondHole = 10;
      } else {
        if (roundCourse.holesToPlay === "back_nine") {
          firstHole = 10;
          secondHole = 14;
        } else {
          firstHole = 1;
          secondHole = 5;
        }
      }
    } else {
      // two round courses signifies two different nines
      const roundCourse1 = roundCourses[0];
      const roundCourse2 = roundCourses[1];
      firstCourse = roundCourse1.course.name;
      secondCourse = roundCourse2.course.name;

      // first hole option from first round course
      firstHole = roundCourse1.holesToPlay === "back_nine" ? 10 : 1;

      // second hole option from second round course
      secondHole = roundCourse2.holesToPlay === "back_nine" ? 10 : 1;
    }

    return [
      {
        label: `Hole ${firstHole}`,
        subLabel: `${firstCourse}`,
        value: { hole: firstHole, course: firstCourse },
      },
      {
        label: `Hole ${secondHole}`,
        subLabel: `${secondCourse}`,
        value: { hole: secondHole, course: secondCourse },
      },
    ];
  };

  //tee times run off the end of the round, so an interval start can always take
  //another one. a shotgun start is fixed to the holes on the course.
  const isIntervalStart = () =>
    ["interval_start", "two_tee_interval_start"].includes(
      currentRound?.startType,
    );

  const addTeeTimes = () => {
    dispatch(addAdditionalHoleAssignment(currentRound?.id));
  };

  const getAddTeeTimes = () => {
    return (
      <div
        ref={addTeeTimesRef}
        className="add-tee-times"
        style={{
          ...defaultLayouts.fullWidth,
          ...defaultLayouts.vStack.align(CENTER, CENTER),
          ...defaultGapping.mediumLargeGap,
          ...defaultPadding.xLargePad,
        }}
      >
        <div
          style={{
            ...defaultTypography.overline2,
            ...defaultTypography.uppercase,
            color: golfstatusColors.grey800,
            textAlign: CENTER,
          }}
        >
          Add additional tee times
        </div>
        <div
          style={{ ...defaultLayouts.fullWidth, position: "relative" }}
        >
          {addingTeeTimes && (
            <GSLoadingSpinnerOverlay
              //small is the button icon's own size, so the spinner takes the
              //plus's place rather than outgrowing it
              spinnerSize="small"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                color: golfstatusColors.white,
              }}
            />
          )}
          <GSButton
            //sized like the + that opens a blocked group back up. the element's
            //own height is a fixed 40px, so it gives way to the padding here
            style={{
              ...defaultLayouts.fullWidth,
              ...defaultBorders.mediumLargeBorderRadius,
              height: "auto",
              padding: `${golfstatusSpacing.mediumLargePad} ${golfstatusSpacing.xLargePad}`,
            }}
            type="black"
            //the plus is hidden rather than dropped for the spinner, since the
            //button takes its height from it, and keeps its color behind it
            buttonIcon={faPlus}
            iconStyle={addingTeeTimes ? { visibility: "hidden" } : undefined}
            onClick={addingTeeTimes ? () => {} : addTeeTimes}
          />
        </div>
      </div>
    );
  };

  const emptyList =
    tournamentTeams === undefined || tournamentTeams?.length === 0;
  const fullList =
    tournamentTeams?.length > 0 && getUnassignedTeams().length === 0;
  const emptySearchResults = getFilteredHoleAssignments().length === 0;

  return (
    <tournament-round-hole-assignment-list
      ref={listRef}
      class={
        emptyList
          ? "empty-container"
          : fullList
          ? "full-container"
          : "default-container"
      }
      style={style}
    >
      <div
        style={{
          ...defaultLayouts.fullWidth,
          ...defaultPadding.xLargePad,
          ...defaultLayouts.vStack,
          ...defaultGapping.xLargeGap,
        }}
      >
        <GSActionBar
          type="action-bar-header"
          header={getAssignedHeader()}
        />
        <div
          style={{
            ...defaultLayouts.fullWidth,
            ...defaultLayouts.vStack,
            ...defaultGapping.largeGap,
            flex: "0 0 auto",
          }}
        >
          <GSInput
            style={{ ...defaultLayouts.fullWidth }}
            placeholder={
              currentRound.startType === "shotgun_start"
                ? "Search Holes, Players & Teams..."
                : "Search Players & Teams..."
            }
            leftIcon={faSearch}
            rightIcon={faTimesCircle}
            rightIconClick={() => {
              setSearch("");
            }}
            textValue={search}
            onChange={(e) => {
              setSearch(e?.target?.value);
            }}
          ></GSInput>
          {currentRound.startType === "two_tee_interval_start" &&
            getTwoTeeHoleOptionsForRoundCourse(currentRound?.roundCourses) !==
              null && (
              <GSSelect
                isClearable
                options={getTwoTeeHoleOptionsForRoundCourse(
                  currentRound?.roundCourses,
                )}
                placeholder="Select Hole..."
                onChange={(option) => {
                  setHoleFilter(option?.value);
                }}
              ></GSSelect>
            )}
        </div>
      </div>
      <GSItemList
        style={
          emptyList || emptySearchResults
            ? {
                ...defaultLayouts.fill,
                justifyContent: CENTER,
                alignItems: CENTER,
              }
            : isPhone()
            ? {
                ...defaultLayouts.fill,
                overflow: "visible",
                borderRight: "none",
              }
            : {
                ...defaultLayouts.fill,
                flex: "1 1 auto",
                minHeight: 0,
                overflow: "auto",
              }
        }
        items={emptyList ? [] : getListItems()}
        listItem={(item) => getHoleAssignment(item)}
        emptyMessage={
          tournamentTeams.length > 0
            ? getNoSearchResultsEmptyListMessage()
            : getNoTeamsEmptyListMessage()
        }
        loading={loading?.includes("getTournamentTeams")}
        loadingMainText="Loading Hole Assignments..."
      />
    </tournament-round-hole-assignment-list>
  );
};

export default TournamentRoundHoleAssignmentList;
